import { useEffect, useState } from 'react';
import type { ClientBundle, OverviewPayload } from '../types';
import { Sidebar, type NavId } from '../components/Sidebar';
import { Dashboard } from '../components/Dashboard';
import { HeatmapView } from '../components/HeatmapView';
import { CompetitorPanel } from '../components/CompetitorPanel';
import { AIScanPanel } from '../components/AIScanPanel';
import { CitationsScreen } from '../components/CitationsScreen';
import { SettingsScreen } from '../components/SettingsScreen';
import { GBPOptimizationPanel } from '../components/GBPOptimizationPanel';
import { useAuth } from '../lib/auth';
import { useIsMobile } from '../lib/useIsMobile';

interface ClientDashboardProps {
  // The public share route embeds the Dashboard overview on the bundle, because
  // an anonymous viewer can't call the authed /overview route. Signed-in users
  // get `undefined` here and the Dashboard fetches it itself.
  bundle: ClientBundle & { overview?: OverviewPayload | null };
  // Read-only chrome (no Share, no AI scan trigger). Used by:
  //   - public share link (no auth)
  //   - client-role users (logged in but restricted)
  readOnly?: boolean;
  onShare?: () => void;
  onRefreshPlace?: () => Promise<void>;
  onRefreshLfAll?: () => Promise<void>;
  onRefreshLfKeyword?: (keyword: string) => Promise<void>;
  onSyncAI?: () => Promise<void>;
  onGenerateInsights?: () => Promise<void>;
  onReloadBundle?: () => void | Promise<void>;
}

export function ClientDashboard({
  bundle, readOnly = false, onShare,
  onRefreshPlace, onRefreshLfAll, onRefreshLfKeyword,
  onSyncAI, onGenerateInsights, onReloadBundle,
}: ClientDashboardProps) {
  const [activeNav, setActiveNav] = useState<NavId>('dashboard');
  const overview = bundle.overview ?? null;
  const { user, logout } = useAuth();
  const isMobile = useIsMobile();
  const [drawerOpen, setDrawerOpen] = useState(false);

  // The drawer used to be dismissible only by tapping the scrim, and that tap
  // does not always produce a click on iOS Safari (a plain <div> with no
  // cursor:pointer never becomes a click target), which left people stuck with
  // the menu open. Now: an explicit ✕, Escape, the scrim on pointerdown, and
  // picking any nav item all close it. While it is open the page behind it is
  // frozen so the scrim can't be scrolled past.
  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setDrawerOpen(false); };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [drawerOpen]);

  // Restrict navigable pages for signed-in users by role.
  // Anonymous viewers (share link, user==null) get the share-link defaults: dashboard + heatmap + competitors + citations.
  const SHARE_LINK_PAGES: NavId[] = ['dashboard', 'analysis', 'heatmap', 'competitors'];
  const allowedPages = user
    ? (user.allowedPages.filter((p): p is NavId => p !== 'users') as NavId[])
    : SHARE_LINK_PAGES;

  // If the current activeNav isn't allowed, snap to dashboard.
  const safeActive: NavId = allowedPages.includes(activeNav) ? activeNav : 'dashboard';

  const screens: Record<NavId, JSX.Element> = {
    dashboard:   <Dashboard data={bundle.dashboard} onNav={setActiveNav} readOnly={readOnly} onShare={onShare} placeData={bundle.placeData} onRefreshPlace={onRefreshPlace} clientId={bundle.client.id} overview={overview} allowedNav={allowedPages} />,
    scan:        <AIScanPanel client={bundle.client} aiScans={bundle.aiScans} aiKeywords={bundle.aiKeywords} lfScans={bundle.lfScans} readOnly={readOnly} onSync={onSyncAI} placeDisplayName={bundle.placeData?.displayName ?? null} />,
    analysis:    <GBPOptimizationPanel clientId={bundle.client.id} clientName={bundle.client.name} readOnly={readOnly} />,
    heatmap:     <HeatmapView data={bundle.heatmap} lfScans={bundle.lfScans} onRefreshLfAll={onRefreshLfAll} onRefreshLfKeyword={onRefreshLfKeyword} onNav={setActiveNav} readOnly={readOnly} />,
    competitors: <CompetitorPanel client={bundle.client} lfScans={bundle.lfScans} />,
    citations:   <CitationsScreen sources={bundle.citations} />,
    settings:    <SettingsScreen client={bundle.client} aiKeywords={bundle.aiKeywords} readOnly={readOnly} onKeywordsChanged={onReloadBundle} />,
  };

  // Sidebar identity: prefer logged-in user; fall back to anonymous label for share-link viewers.
  const userInitial = user?.name?.[0] ?? user?.email?.[0]?.toUpperCase() ?? 'C';
  const userName = user?.name ?? (user ? user.email : 'Client view');
  const userPlan = user
    ? user.role.charAt(0).toUpperCase() + user.role.slice(1)
    : 'Shared link';

  const sidebar = (
    <Sidebar
      active={safeActive}
      onNav={(id) => { setActiveNav(id); setDrawerOpen(false); }}
      client={bundle.client}
      readOnly={readOnly}
      allowedPages={allowedPages as unknown as import('../types').PageId[]}
      userInitial={userInitial}
      userName={userName}
      userPlan={userPlan}
      onLogout={user ? logout : undefined}
    />
  );

  // Phone: the 220px sidebar becomes an off-canvas drawer behind a hamburger,
  // and the screen flows naturally so the whole page scrolls.
  if (isMobile) {
    return (
      <div style={{ minHeight: '100%', display: 'flex', flexDirection: 'column', background: '#fff' }}>
        <div style={{
          position: 'sticky', top: 0, zIndex: 60,
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '10px 14px', background: '#fff', borderBottom: '1px solid #dadce0',
        }}>
          <button onClick={() => setDrawerOpen(true)} aria-label="Open menu" style={{
            background: 'transparent', border: '1px solid #dadce0', borderRadius: 8,
            padding: '6px 11px', fontSize: 16, lineHeight: 1, cursor: 'pointer',
          }}>☰</button>
          <div style={{
            background: '#fff', borderRadius: 6, padding: '4px 6px',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <img src="/assets/gbi-logo.png" alt="GBI" style={{ height: 24, width: 'auto', display: 'block' }} />
          </div>
          <div style={{ fontWeight: 700, fontSize: 13, color: '#202124', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {bundle.client.name}
          </div>
        </div>
        {drawerOpen && (
          <div
            onPointerDown={() => setDrawerOpen(false)}
            onClick={() => setDrawerOpen(false)}
            aria-label="Close menu"
            style={{
              position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 70,
              cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
            }}
          />
        )}
        <div
          aria-hidden={!drawerOpen}
          style={{
            position: 'fixed', top: 0, bottom: 0, left: 0, width: 240, zIndex: 80,
            transform: drawerOpen ? 'translateX(0)' : 'translateX(-100%)',
            transition: 'transform 220ms ease',
            boxShadow: drawerOpen ? '0 0 28px rgba(0,0,0,0.25)' : 'none',
            // Off-screen it must not swallow taps meant for the page.
            pointerEvents: drawerOpen ? 'auto' : 'none',
          }}
        >
          {sidebar}
          {drawerOpen && (
            <button
              onClick={() => setDrawerOpen(false)}
              aria-label="Close menu"
              style={{
                position: 'absolute', top: 12, right: 10, zIndex: 1,
                width: 32, height: 32, borderRadius: 8,
                background: '#fff', border: '1px solid #dadce0', color: '#5f6368',
                fontSize: 15, lineHeight: 1, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >✕</button>
          )}
        </div>
        <div style={{ minWidth: 0, width: '100%' }}>
          {screens[safeActive]}
        </div>
      </div>
    );
  }

  return (
    // height:100% (not 100vh) so this fills whatever vertical space the parent
    // gives it. AdminClientView wraps it in a flex column under the NavHeader;
    // PublicShareView gives it a 100vh box. Using 100vh here double-counted the
    // NavHeader height and pushed the bottom off-screen.
    <div style={{ display: 'flex', height: '100%', minHeight: 0, overflow: 'hidden' }}>
      {sidebar}
      {screens[safeActive]}
    </div>
  );
}
